import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { message } from "antd";
import {
  WORKSPACE_NAME_PATTERN,
  WORKSPACE_NAME_HINT,
} from "../config/constants";
import {
  fetchAllIdClientsAction,
  fetchAllMarketsAction,
  fetchRefreshAction,
  fetchSKUCompaniesAction,
  appendGeoscopeMarketsAction,
  clearMarkets,
  clearRefreshData,
  clearSKUCompanies,
  clearCompanyBrands,
} from "../../../../slices/internalToolSlice";
import {
  removeGeoscopeMarkets,
  deleteGeoscopeRegion,
} from "../../../../api/internal_tool_endpoints";
import {
  fetchCompaniesWithBrandsData,
  clearCompetitors,
} from "../../../../slices/competitorsSlice";
import {
  createWorkspaceAction,
  fetchAllWorkspacesAction,
} from "../../../../slices/workspaceSlice";
import { useMarketConfig } from "../../hooks/useMarketConfig";

export const useWorkspaceForm = (open, onSuccess) => {
  const dispatch = useDispatch();
  const {
    clientIds,
    clientIdsLoading,
    markets,
    marketsLoading,
    refreshData,
    refreshLoading,
    refreshError,
    refreshErrorStatus,
    skuCompanies,
  } = useSelector((state) => state.internalTool);

  const { loadingByCountry: companiesWithBrandsLoadingByCountry } = useSelector(
    (state) => state.competitors
  );

  const {
    loading: workspaceLoading,
    error: workspaceError,
    success: workspaceSuccess,
    allWorkspaces,
    fetchingWorkspaces,
    fetchWorkspacesError,
  } = useSelector((state) => state.workspace);

  const [currentStep, setCurrentStep] = useState(0);
  const [workspaceName, setWorkspaceName] = useState("");
  const [clientId, setClientId] = useState("");
  const [customRegions, setCustomRegions] = useState({});
  const [newRegionName, setNewRegionName] = useState("");

  // New state for additional requirements
  const [refreshPeriods, setRefreshPeriods] = useState([]);
  // Monthly delivery is the norm; one-time is the opt-out. Default ON so the
  // need_auto_refresh flag reflects reality without staff having to remember it.
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [contractMonthRange, setContractMonthRange] = useState([null, null]);

  // Shared per-market weights + assignments surface (Step2 + Step3).
  const marketConfig = useMarketConfig({ clientId, customRegions });
  const {
    marketAssignments,
    getPairs,
    calcTotal,
    buildMarketConfig,
    resetMarketConfig,
    companiesWithBrands,
  } = marketConfig;

  const handleWorkspaceNameChange = useCallback((e) => {
    setWorkspaceName(e.target.value);
  }, []);

  // Inline-validation flag for the workspace-name field. Empty input is handled
  // by the required-check in validateStep, so only flag non-empty malformed values.
  const workspaceNameInvalid = useMemo(() => {
    const name = workspaceName.trim();
    return name.length > 0 && !WORKSPACE_NAME_PATTERN.test(name);
  }, [workspaceName]);

  // The workspace name IS the id_client both writes are keyed on, so a name
  // already in use would land on an existing workspace. Compared lower-cased:
  // stored ids are canonically lowercase but legacy rows are not, and they name
  // the same workspace.
  const existingWorkspaceNames = useMemo(() => {
    const names = new Set();
    (allWorkspaces || []).forEach((w) => {
      if (w?.id_client) names.add(String(w.id_client).toLowerCase());
    });
    return names;
  }, [allWorkspaces]);

  // Each list is fetched at most once per opening. Retrying while the list is
  // empty loops forever when the fetch fails: a rejected reducer that assigns a
  // fresh [] gives it a new identity, which refires an emptiness-guarded effect,
  // until React aborts with "Maximum update depth exceeded" and the modal's
  // portal tears down. Reset on close so reopening retries.
  const fetchedOnOpenRef = useRef({ clients: false, workspaces: false });

  // Own the list the name check gates on rather than relying on the page having
  // fetched it, so an empty list is known to mean "no workspaces exist" rather
  // than "not loaded yet" -- an environment with none must still be able to
  // create its first.
  useEffect(() => {
    if (!open || fetchedOnOpenRef.current.workspaces) return;
    if (allWorkspaces && allWorkspaces.length > 0) return;
    fetchedOnOpenRef.current.workspaces = true;
    dispatch(fetchAllWorkspacesAction());
  }, [open, dispatch, allWorkspaces]);

  // Whether the list can be trusted to answer "is this name free?". A load that
  // is still running or has failed must block rather than wave a name through;
  // a settled empty list is a real answer and lets every name pass.
  const canCheckWorkspaceNames = !fetchingWorkspaces && !fetchWorkspacesError;

  const workspaceNameTaken = useMemo(() => {
    const name = workspaceName.trim().toLowerCase();
    return name.length > 0 && existingWorkspaceNames.has(name);
  }, [workspaceName, existingWorkspaceNames]);

  const handleClientIdChange = useCallback(
    (val) => {
      setClientId(val || "");
      setCustomRegions({});
      resetMarketConfig();

      // Reset refresh-related state when switching clients
      setRefreshPeriods([]);
      setAutoRefresh(true); // monthly is the default cadence

      // Fetch markets and refresh data for the selected client or clear if no client
      if (val) {
        dispatch(fetchAllMarketsAction({ id_client: val }));
        dispatch(fetchRefreshAction({ id_client: val }));
      } else {
        dispatch(clearMarkets());
        dispatch(clearRefreshData());
        dispatch(clearSKUCompanies());
        dispatch(clearCompanyBrands());
      }
    },
    [dispatch, resetMarketConfig]
  );

  const handleNewRegionNameChange = useCallback((e) => {
    setNewRegionName(e.target.value);
  }, []);

  const addRegion = useCallback(() => {
    const name = (newRegionName || "").trim();
    if (!name) return;
    const id = "r_" + Date.now();
    setCustomRegions((p) => ({
      ...p,
      [id]: { name, markets: [] },
    }));
    setNewRegionName(""); // Clear the input
  }, [newRegionName]);

  const handleNewRegionNamePressEnter = useCallback(
    (e) => {
      if (e?.nativeEvent?.isComposing) return;
      addRegion();
    },
    [addRegion]
  );

  // New handlers for additional requirements
  const handleRefreshPeriodsChange = useCallback((values) => {
    setRefreshPeriods(values || []);
  }, []);

  const handleAutoRefreshChange = useCallback((checked) => {
    setAutoRefresh(checked);
  }, []);

  const handleContractMonthRangeChange = useCallback((dates) => {
    setContractMonthRange(dates || [null, null]);
  }, []);

  // Fetch client IDs when modal opens
  useEffect(() => {
    if (!open || fetchedOnOpenRef.current.clients) return;
    if (clientIds && clientIds.length > 0) return;
    fetchedOnOpenRef.current.clients = true;
    dispatch(fetchAllIdClientsAction());
  }, [open, dispatch, clientIds]);

  useEffect(() => {
    if (!open) {
      fetchedOnOpenRef.current = { clients: false, workspaces: false };
      setCurrentStep(0);
      setWorkspaceName("");
      setClientId("");
      setCustomRegions({});
      setNewRegionName("");
      resetMarketConfig();
      setRefreshPeriods([]);
      setAutoRefresh(true); // monthly is the default cadence
      setContractMonthRange([null, null]);
      dispatch(clearCompetitors());
    }
  }, [open, dispatch, resetMarketConfig]);

  // Shape the shared Step4 expects. Matches useAddMarketForm, which builds the
  // same object from its own selected client id.
  const client = useMemo(
    () => (clientId ? { name: clientId } : undefined),
    [clientId]
  );

  const allClientMarkets = useMemo(() => {
    if (markets && Array.isArray(markets) && markets.length > 0) {
      return markets;
    }
    return [];
  }, [markets]);

  const marketOptions = useMemo(
    () =>
      allClientMarkets.map((m) => ({
        value: m,
        label: m.toUpperCase(),
      })),
    [allClientMarkets]
  );

  const removeRegion = useCallback(
    (rid) =>
      setCustomRegions((p) => {
        const c = { ...p };
        delete c[rid];
        return c;
      }),
    []
  );

  const updateRegionMarkets = useCallback((rid, markets) => {
    setCustomRegions((p) => ({
      ...p,
      [rid]: { ...(p[rid] || {}), markets: markets || [] },
    }));
  }, []);

  // Fetch SKU companies when reaching step 3 and we have clientId and markets
  useEffect(() => {
    if (currentStep === 2 && clientId && open) {
      // Step 3 is index 2
      const pairs = getPairs();
      const uniqueCountries = new Set();

      pairs.forEach(({ market }) => {
        uniqueCountries.add(market);
      });

      // Only fetch companies for countries not yet loaded
      uniqueCountries.forEach((country) => {
        if (!skuCompanies?.[country]) {
          dispatch(fetchSKUCompaniesAction({ id_client: clientId, country }));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, clientId, open, dispatch, getPairs]);

  // Auto-fetch competitors data for any market that has a company selected but no data yet
  useEffect(() => {
    if (currentStep === 2 && clientId && open) {
      const pairs = getPairs();
      pairs.forEach(({ regionId, market }) => {
        const ck = `${regionId}|${market}`;
        const company = marketAssignments[ck]?.company;
        const alreadyLoading = !!companiesWithBrandsLoadingByCountry?.[market];
        const hasData = companiesWithBrands?.[market] != null;
        if (company && !hasData && !alreadyLoading) {
          dispatch(fetchCompaniesWithBrandsData({ id_client: clientId, country: market }));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, clientId, open, dispatch, getPairs, marketAssignments]);

  // Transform workspace data to API format
  const transformWorkspaceData = useCallback(() => {
    const pairs = getPairs();
    const countryConfigurations = {};

    pairs.forEach(({ regionId, market }) => {
      const ck = `${regionId}|${market}`;
      if (!marketAssignments[ck]) return;
      countryConfigurations[market.toUpperCase()] = buildMarketConfig(ck);
    });

    return {
      version_name: workspaceName,
      comment: null,
      refresh: refreshPeriods || [],
      need_auto_refresh: autoRefresh || false,
      start_end_month: (contractMonthRange?.[0] && contractMonthRange?.[1])
        ? [
            contractMonthRange[0].format("YYYY-MM"),
            contractMonthRange[1].format("YYYY-MM"),
          ]
        : null,
      mapped_id_client: clientId,
      country_configurations: countryConfigurations,
    };
  }, [
    marketAssignments,
    buildMarketConfig,
    workspaceName,
    refreshPeriods,
    autoRefresh,
    contractMonthRange,
    clientId,
    getPairs,
  ]);

  // Transform custom regions data to API format. Country codes are uppercased
  // to match the keys transformWorkspaceData writes: pet_formula matches a
  // country against its region's markets exactly, and relying on the picker's
  // values already being uppercase couples this to the lookup endpoint.
  const transformRegionsData = useCallback(() => {
    const regions = Object.values(customRegions || {})
      .filter((region) => region.markets && region.markets.length > 0)
      .map((region) => ({
        region: region.name,
        country_codes: (region.markets || []).map((m) =>
          String(m).toUpperCase()
        ),
      }));

    return { regions };
  }, [customRegions]);

  const clientOptions = useMemo(() => {
    // Handle plain array returned from API (e.g. ["asahi", "britvic", ...])
    if (clientIds && Array.isArray(clientIds) && clientIds.length > 0) {
      return clientIds.map((id) => ({ value: id, label: id }));
    }

    // Handle { id_client: [...] } format
    if (
      clientIds &&
      clientIds.id_client &&
      Array.isArray(clientIds.id_client)
    ) {
      return clientIds.id_client.map((id) => ({ value: id, label: id }));
    }

    // No options rather than placeholders: a picked placeholder was submitted
    // verbatim as mapped_id_client and written to dwd_id_client_mapping. The
    // empty state is rendered by Step1's notFoundContent.
    return [];
  }, [clientIds]);

  // Refresh periods are the client's real YYYYMM values from
  // dws_outlet_refresh_summary. There is deliberately no fallback: a generated
  // list matches no data, and the backend rejects anything that is not YYYYMM.
  const refreshPeriodOptions = useMemo(() => {
    if (refreshData && Array.isArray(refreshData) && refreshData.length > 0) {
      return refreshData.map((period) => ({
        value: period,
        label: period,
      }));
    }
    return [];
  }, [refreshData]);

  // 404 means the client genuinely has no refresh data — the normal case for a
  // client whose first workspace this is, and a reason to pick a different
  // client rather than retry. Any other failure is retryable. Kept apart so the
  // field can say which, instead of showing one ambiguous empty state.
  const refreshUnavailable = refreshErrorStatus === 404;
  const refreshFailed = !!refreshError && !refreshUnavailable;

  const prev = () => setCurrentStep((s) => Math.max(0, s - 1));

  // Undo the geoscope append after a failed pet_formula write, so a failed
  // submit leaves the workspace exactly as it was. Drops a whole region when
  // this submit created it, otherwise takes back only the codes it added. A
  // create can map several regions at once, so every one of them is undone.
  const rollbackGeoscope = async (workspaceIdentifier, appended) => {
    if (!appended?.length) return true;
    let allUndone = true;
    for (const region of appended) {
      try {
        if (region.created) {
          await deleteGeoscopeRegion({
            id_client: workspaceIdentifier,
            region: region.region_key,
          });
        } else if (region.added_markets?.length) {
          await removeGeoscopeMarkets({
            id_client: workspaceIdentifier,
            region: region.region_key,
            country_codes: region.added_markets,
          });
        }
      } catch (rollbackError) {
        console.error("❌ Failed to roll back geoscope:", rollbackError);
        allUndone = false;
      }
    }
    return allUndone;
  };

  const finish = async () => {
    const workspaceIdentifier = workspaceName.trim();
    let appended = null;

    try {
      const workspaceData = transformWorkspaceData();
      const regionsData = transformRegionsData();

      // 1) Map the markets into their regions. This goes first because it is
      //    the reversible half: step 2 schedules a pipeline run that cannot be
      //    undone, so it must be the last thing that happens. The append
      //    endpoint is idempotent and creates the document when absent, so a
      //    retry after a failed run works under the same name -- the create-only
      //    endpoint answered 409 instead and locked the name out.
      if (regionsData.regions && regionsData.regions.length > 0) {
        const appendResult = await dispatch(
          appendGeoscopeMarketsAction({
            id_client: workspaceIdentifier,
            regions_info: regionsData,
          })
        ).unwrap();
        appended = appendResult?.regions || null;
      }

      // 2) Create the workspace's pet_formula. Writes the first master version
      //    and triggers the pipeline.
      const result = await dispatch(
        createWorkspaceAction({
          id_client: workspaceIdentifier,
          workspace_info: workspaceData,
        })
      ).unwrap();

      message.success("Workspace created successfully!");
      onSuccess?.(result);
    } catch (error) {
      console.error("❌ Failed to create workspace:", error);
      const detail = error?.message || String(error);

      // A status means the server answered and rejected the write, so nothing
      // was applied and the earlier geoscope append is safe to undo. Without
      // one (timeout, dropped connection) the outcome is unknown: undoing could
      // strip the region mapping from a workspace whose version was in fact
      // created, so leave it alone and tell the operator to check.
      if (appended?.length && typeof error?.status !== "number") {
        message.error(
          `Create workspace did not complete for ${workspaceIdentifier}: ` +
            `${detail}. The result is unknown, so nothing was rolled back — ` +
            `check ${workspaceIdentifier} in the table before retrying.`,
          12
        );
        return;
      }

      if (appended?.length) {
        const undone = await rollbackGeoscope(workspaceIdentifier, appended);
        const regionNames = appended
          .map((r) => r.region_name || r.region_key)
          .join(", ");
        message.error(
          undone
            ? `Failed to create workspace: ${detail}. No changes were saved.`
            : `Failed to create workspace: ${detail}. Rolling back the region ` +
                `changes also failed, so ${workspaceIdentifier} may still ` +
                `have ${regionNames} configured — remove them before ` +
                `retrying.`,
          undone ? 8 : 12
        );
        return;
      }

      message.error(`Failed to create workspace: ${detail}`);
    }
  };

  const validateStep = (step) => {
    if (step === 0) {
      const name = workspaceName.trim();
      if (!name) {
        message.error("Please enter workspace name");
        return false;
      }
      if (!WORKSPACE_NAME_PATTERN.test(name)) {
        message.error(`Invalid workspace name. ${WORKSPACE_NAME_HINT}`);
        return false;
      }
      // Reject a name already in use before any write. The backend answers 409
      // on the same condition; this keeps the operator from getting there with
      // a half-filled wizard.
      if (!canCheckWorkspaceNames) {
        message.error(
          "Cannot verify the workspace name is unique — the workspace list " +
            "has not loaded. Reload the page and try again."
        );
        return false;
      }
      if (workspaceNameTaken) {
        message.error(
          `Workspace "${name}" already exists. Pick a different name.`
        );
        return false;
      }
      if (!clientId) {
        message.error("Please select a client");
        return false;
      }
      const has = Object.values(customRegions || {}).some(
        (r) => (r.markets || []).length > 0
      );
      if (!has) {
        message.error("Add at least one custom region with markets");
        return false;
      }
      // Refresh periods are deliberately optional. The stored list is a record
      // of what the operator selected, not an input to anything: the pipeline
      // trigger never puts it in the DAG conf, and the Refresh Monitor reads the
      // periods a workspace actually has from ClickHouse
      // (dws_pet_performance_refresh_summary) rather than from this field. Only
      // its sibling need_auto_refresh drives behaviour. Blocking submission on it
      // would cost the operator a workspace and buy no correctness.

      // Validate contract month range if provided
      if (
        contractMonthRange &&
        contractMonthRange[0] &&
        contractMonthRange[1]
      ) {
        if (contractMonthRange[0].isAfter(contractMonthRange[1])) {
          message.error("Contract start month must be before end month");
          return false;
        }
      }

      return true;
    }
    if (step === 1) {
      const pairs = getPairs();
      const ok = pairs.every(
        ({ regionId, market }) =>
          Math.abs(calcTotal(`${regionId}|${market}`) - 100) < 0.1
      );
      if (!ok) {
        message.error("All market KPI weights must total 100%");
        return false;
      }
      return true;
    }
    if (step === 2) {
      const pairs = getPairs();

      // Check if all markets have complete assignments
      const incompleteMarkets = pairs.filter(({ regionId, market }) => {
        const ck = `${regionId}|${market}`;
        const assignment = marketAssignments[ck];

        // Check if company is selected
        if (!assignment?.company) {
          return true;
        }

        // Check if at least one core brand group is created
        if (
          !assignment?.coreBrandGroups ||
          assignment.coreBrandGroups.length === 0
        ) {
          return true;
        }

        // Competitors are required on creation. The SQL that builds a market's
        // performance data assumes they exist and renders an invalid CASE
        // without them, so a workspace created with none fails every pipeline
        // run and can never be built. Editing an existing market does not
        // require them, so markets configured before this rule stay editable.
        if (!assignment?.competitors || assignment.competitors.length === 0) {
          return true;
        }

        return false;
      });

      if (incompleteMarkets.length > 0) {
        const marketNames = incompleteMarkets
          .map(
            ({ regionName, market }) =>
              `${regionName} - ${market.toUpperCase()}`
          )
          .join(", ");

        message.error(
          `Please complete assignments for all markets. Missing: ${marketNames}`
        );
        return false;
      }

      return true;
    }
    return true;
  };

  const next = () => {
    if (!validateStep(currentStep)) return;

    if (currentStep < 3) {
      setCurrentStep((s) => s + 1);
    } else {
      finish();
    }
  };

  return {
    // shared market-config surface (Step2 + Step3)
    ...marketConfig,
    // create-flow Step1 + meta
    currentStep,
    workspaceName,
    workspaceNameInvalid,
    workspaceNameTaken,
    // Creation requires a competitor per market; validateStep enforces it and
    // the shared Step3 reads this to mark the market and label the field. Kept
    // on the hook so the gate and the indicator cannot disagree. Add Market
    // doesn't return it, so it keeps the false default.
    competitorsRequired: true,
    clientId,
    customRegions,
    newRegionName,
    refreshPeriods,
    autoRefresh,
    contractMonthRange,
    client,
    allClientMarkets,
    marketOptions,
    clientOptions,
    refreshPeriodOptions,
    refreshUnavailable,
    refreshFailed,
    handleWorkspaceNameChange,
    handleClientIdChange,
    handleNewRegionNameChange,
    addRegion,
    handleNewRegionNamePressEnter,
    removeRegion,
    updateRegionMarkets,
    handleRefreshPeriodsChange,
    handleAutoRefreshChange,
    handleContractMonthRangeChange,
    prev,
    next,
    finish,
    // Redux state for client IDs
    clientIds,
    clientIdsLoading,
    // Redux state for markets
    markets,
    marketsLoading,
    // Redux state for refresh data
    refreshData,
    refreshLoading,
    refreshError,
    // Workspace creation state
    workspaceLoading,
    workspaceError,
    workspaceSuccess,
  };
};
